import {
  Button,
  ButtonBase,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  Close as CloseIcon,
  SentimentNeutralOutlined as SentimentNeutralOutlinedIcon,
  SentimentVeryDissatisfiedOutlined as SentimentVeryDissatisfiedOutlinedIcon,
  SentimentVerySatisfiedOutlined as SentimentVerySatisfiedOutlinedIcon,
} from "@mui/icons-material";
import type { ExperienceRating } from "../../types";
import { ACCENT_COLOR } from "../../styles";

type ExperienceDialogProps = {
  open: boolean;
  onClose: () => void;
  rating: ExperienceRating;
  onRatingChange: (value: ExperienceRating) => void;
  notes: string;
  onNotesChange: (value: string) => void;
  onSave: () => void;
  hasExperience: boolean;
};

const ratingOptions = [
  {
    value: "sad" as ExperienceRating,
    label: "Sad",
    emoji: "😞",
    icon: (
      <SentimentVeryDissatisfiedOutlinedIcon
        sx={{ fontSize: 32, color: "#111827" }}
      />
    ),
  },
  {
    value: "neutral" as ExperienceRating,
    label: "Neutral",
    emoji: "😬",
    icon: <SentimentNeutralOutlinedIcon sx={{ fontSize: 32, color: "#111827" }} />,
  },
  {
    value: "happy" as ExperienceRating,
    label: "Happy",
    emoji: "😍",
    icon: (
      <SentimentVerySatisfiedOutlinedIcon
        sx={{ fontSize: 32, color: "#111827" }}
      />
    ),
  },
];

const ExperienceDialog = ({
  open,
  onClose,
  rating,
  onRatingChange,
  notes,
  onNotesChange,
  onSave,
  hasExperience,
}: ExperienceDialogProps) => (
  <Dialog
    open={open}
    onClose={onClose}
    fullWidth
    maxWidth="sm"
    PaperProps={{
      sx: {
        borderRadius: "20px",
        px: { xs: 2.5, sm: 3 },
        py: { xs: 2, sm: 2.5 },
      },
    }}
  >
    <DialogTitle
      sx={{
        p: 0,
        mb: 2,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}
    >
      <Typography variant="h6" sx={{ fontWeight: 700 }}>
        Rate your experience
      </Typography>
      <IconButton onClick={onClose}>
        <CloseIcon />
      </IconButton>
    </DialogTitle>
    <DialogContent sx={{ p: 0 }}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={2}>
          {ratingOptions.map((option) => {
            const isSelected = rating === option.value;
            return (
              <ButtonBase
                key={option.value}
                onClick={() => onRatingChange(option.value)}
                aria-pressed={isSelected}
                aria-label={option.label}
                sx={{
                  flex: 1,
                  borderRadius: "16px",
                  border: "none",
                  height: 110,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  backgroundColor: isSelected ? "#F3F0FF" : "#fff",
                  transition: "all 150ms ease",
                }}
              >
                {isSelected ? (
                  <Typography sx={{ fontSize: 32 }}>{option.emoji}</Typography>
                ) : (
                  option.icon
                )}
              </ButtonBase>
            );
          })}
        </Stack>
        <Typography sx={{ fontWeight: 600, color: "#111827" }}>
          Thanks, what is the reason for your rating?
        </Typography>
        <TextField
          value={notes}
          onChange={(event) => onNotesChange(event.target.value)}
          multiline
          minRows={4}
          fullWidth
          placeholder="Describe why it was excellent"
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              backgroundColor: "#fff",
              "& fieldset": { borderColor: "#E5E7EB" },
              "&:hover fieldset": { borderColor: "#C7CEDD" },
              "&.Mui-focused fieldset": { borderColor: ACCENT_COLOR },
            },
            "& .MuiOutlinedInput-input": {
              fontSize: "14px",
              lineHeight: 1.6,
            },
          }}
        />
        <Button
          variant="contained"
          onClick={onSave}
          fullWidth
          sx={{
            textTransform: "none",
            backgroundColor: "#1C1C1C",
            borderRadius: "12px",
            py: 1.2,
            fontWeight: 600,
            "&:hover": { backgroundColor: "#1C1C1C" },
          }}
        >
          {hasExperience ? "Update Experience" : "Add Experience"}
        </Button>
      </Stack>
    </DialogContent>
  </Dialog>
);

export default ExperienceDialog;
